// Звук игры: сэмплы (public/audio, генерятся tools/make-audio.mjs) + микшер
// (музыка/эффекты раздельно, дакинг) + трёхслойная музыка в ритме маятника.
// Пока сэмплы не догрузились (или сеть их не отдала) — работает старый
// синтезатор: звук деградирует изящно, а не пропадает.
// Загрузка ленивая, после первого тапа: политика автоплея вебвью.

let ctx = null;
let sfxBus = null;      // шина эффектов
let musicBus = null;    // шина музыки (дакинг давит её же)
let ambientGain = null; // эмбиент сидит на музыкальной шине со своим уровнем

let muted = false;
let volSfx = 1;
let volMusic = 1;
try {
  muted = localStorage.getItem('tower_muted') === '1';
  volSfx = clamp01(parseFloat(localStorage.getItem('tower_vol_sfx') ?? '1'));
  volMusic = clamp01(parseFloat(localStorage.getItem('tower_vol_music') ?? '0.8'));
} catch (e) { /* приватный режим */ }

function clamp01(v) { return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 1; }

// --- Сэмплы ---
const SAMPLE_NAMES = [
  'land1', 'land2', 'land3', 'perfect', 'cut', 'crash', 'release', 'creak',
  'golden', 'checkpoint', 'helicopter', 'fanfare', 'death', 'whoosh', 'applause',
  'ambient_day', 'ambient_night', 'music_base', 'music_mel', 'music_top',
];
const buffers = {};
let loading = false;
let onReadyQueue = []; // музыка могла попроситься до загрузки — стартуем после

function sampleExt() {
  const a = document.createElement('audio');
  return a.canPlayType && a.canPlayType('audio/ogg; codecs="vorbis"') ? '.ogg' : '.m4a';
}

function loadSamples() {
  if (loading || !ctx) return;
  loading = true;
  const ext = sampleExt();
  const v = (window.__BUILD__ || '1');
  let left = SAMPLE_NAMES.length;
  for (const name of SAMPLE_NAMES) {
    fetch(`/audio/${name}${ext}?v=${v}`)
      .then((r) => { if (!r.ok) throw new Error(`http ${r.status}`); return r.arrayBuffer(); })
      .then((ab) => ctx.decodeAudioData(ab))
      .then((buf) => { buffers[name] = buf; })
      .catch(() => { /* останется синтезатор */ })
      .finally(() => {
        left -= 1;
        if (left === 0) {
          const q = onReadyQueue; onReadyQueue = [];
          q.forEach((fn) => { try { fn(); } catch (e) { /* не критично */ } });
        }
      });
  }
}

export function unlockAudio() {
  if (!ctx) {
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      sfxBus = ctx.createGain();
      musicBus = ctx.createGain();
      ambientGain = ctx.createGain();
      sfxBus.connect(ctx.destination);
      musicBus.connect(ctx.destination);
      ambientGain.connect(musicBus);
      applyVolumes();
      loadSamples();
    } catch (e) {
      ctx = null; // WebAudio недоступен — играем молча
    }
  }
  if (ctx && ctx.state === 'suspended') ctx.resume();
}

function applyVolumes() {
  if (!ctx) return;
  const m = muted ? 0 : 1;
  sfxBus.gain.setTargetAtTime(volSfx * m, ctx.currentTime, 0.02);
  musicBus.gain.setTargetAtTime(volMusic * m * duckFactor, ctx.currentTime, 0.05);
}

export function setMuted(value) {
  muted = value;
  try { localStorage.setItem('tower_muted', value ? '1' : '0'); } catch (e) { /* ок */ }
  applyVolumes();
}
export function isMuted() { return muted; }

// Раздельные громкости — «Микшер» из ТЗ. Хранение в localStorage.
export function getVolumes() { return { music: volMusic, sfx: volSfx }; }
export function setVolumes({ music, sfx }) {
  if (music != null) volMusic = clamp01(music);
  if (sfx != null) volSfx = clamp01(sfx);
  try {
    localStorage.setItem('tower_vol_music', String(volMusic));
    localStorage.setItem('tower_vol_sfx', String(volSfx));
  } catch (e) { /* ок */ }
  applyVolumes();
}

// Дакинг: на джинглах/фанфарах музыка приседает на −6дБ и возвращается
let duckFactor = 1;
let duckTimer = 0;
function duck(ms = 900) {
  if (!ctx) return;
  duckFactor = 0.5;
  applyVolumes();
  clearTimeout(duckTimer);
  duckTimer = setTimeout(() => { duckFactor = 1; applyVolumes(); }, ms);
}

// Проиграть сэмпл; вернёт false, если буфера ещё нет (сработает фолбэк)
function play(name, { gain = 1, rate = 1, bus = null } = {}) {
  const buf = ctx && buffers[name];
  if (!buf || muted) return false;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = rate;
  const g = ctx.createGain();
  g.gain.value = gain;
  src.connect(g).connect(bus || sfxBus);
  src.start();
  return true;
}

// --- Фолбэк-синтезатор (бывший основной звук) ---
function tone({ freq, type = 'sine', dur = 0.15, gain = 0.25, slideTo = null, delay = 0 }) {
  if (!ctx || muted) return;
  const t0 = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, t0 + dur);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(sfxBus);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
}

function noise({ dur = 0.2, gain = 0.3, freq = 800, q = 1, delay = 0 }) {
  if (!ctx || muted) return;
  const t0 = ctx.currentTime + delay;
  const len = Math.ceil(ctx.sampleRate * dur);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = freq;
  filter.Q.value = q;
  const g = ctx.createGain();
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(filter).connect(g).connect(sfxBus);
  src.start(t0);
}

// --- Скрип крана: тихий луп, пока блок висит на тросе ---
let creakSrc = null;
function creakLoop(on) {
  if (!ctx) return;
  if (on && !creakSrc && buffers.creak && !muted) {
    creakSrc = ctx.createBufferSource();
    creakSrc.buffer = buffers.creak;
    creakSrc.loop = true;
    const g = ctx.createGain();
    g.gain.value = 0.07;
    creakSrc.connect(g).connect(sfxBus);
    creakSrc.start();
    creakSrc._gain = g;
  } else if (!on && creakSrc) {
    const src = creakSrc;
    creakSrc = null;
    try {
      src._gain.gain.setTargetAtTime(0, ctx.currentTime, 0.05);
      src.stop(ctx.currentTime + 0.2);
    } catch (e) { /* уже остановлен */ }
  }
}

export const sfx = {
  // Щёлк отцепления блока
  release() {
    if (!play('release', { gain: 0.7 })) {
      noise({ dur: 0.06, gain: 0.18, freq: 3000 });
      tone({ freq: 520, type: 'square', dur: 0.05, gain: 0.08 });
    }
  },
  // Посадка: 3 варианта бетонного «тудума», случайный + питч ±5%
  thud() {
    const n = 1 + Math.floor(Math.random() * 3);
    if (!play('land' + n, { rate: 0.95 + Math.random() * 0.1 })) {
      tone({ freq: 90, type: 'sine', dur: 0.22, gain: 0.5, slideTo: 45 });
      noise({ dur: 0.12, gain: 0.22, freq: 500 });
    }
  },
  // Срез края: хруст поверх удара
  cut() {
    this.thud();
    play('cut', { gain: 0.8, rate: 0.95 + Math.random() * 0.1 });
  },
  // Дзынь перфекта: питч-лестница — полутон за каждый шаг комбо
  perfect(combo) {
    const rate = Math.pow(1.0595, Math.min(combo, 12));
    play('land' + (1 + Math.floor(Math.random() * 3)), { gain: 0.5 });
    if (!play('perfect', { rate, gain: 0.9 })) {
      const base = 660 * rate;
      tone({ freq: base, type: 'triangle', dur: 0.35, gain: 0.3 });
      tone({ freq: base * 1.5, type: 'sine', dur: 0.4, gain: 0.15, delay: 0.05 });
    }
  },
  // Блок соскользнул и рухнул
  crash() {
    if (!play('crash')) {
      tone({ freq: 160, type: 'sawtooth', dur: 0.3, gain: 0.25, slideTo: 40 });
      noise({ dur: 0.45, gain: 0.4, freq: 300 });
    }
  },
  // Золотой этаж / золотой блок
  golden() {
    duck(900);
    if (!play('golden')) {
      [523, 659, 784, 1047].forEach((f, i) => tone({ freq: f, type: 'triangle', dur: 0.3, gain: 0.2, delay: i * 0.07 }));
    }
  },
  // Чекпоинт каждые 10 этажей — джингл «момента гордости»
  checkpoint() {
    duck(900);
    play('checkpoint');
  },
  // Вертолёт (комбо 10)
  helicopter() {
    if (!play('helicopter')) {
      for (let i = 0; i < 14; i++) noise({ dur: 0.05, gain: 0.12, freq: 250, delay: i * 0.09 });
    }
  },
  // Фанфары победы
  fanfare() {
    duck(2200);
    if (!play('fanfare')) {
      [392, 523, 659, 784].forEach((f, i) => tone({ freq: f, type: 'square', dur: 0.22, gain: 0.16, delay: i * 0.13 }));
      tone({ freq: 1047, type: 'triangle', dur: 0.6, gain: 0.22, delay: 0.52 });
    }
  },
  // Поражение — бас-провал
  fail() {
    if (!play('death')) {
      tone({ freq: 220, type: 'sawtooth', dur: 0.4, gain: 0.2, slideTo: 110 });
      tone({ freq: 165, type: 'sawtooth', dur: 0.6, gain: 0.18, slideTo: 82, delay: 0.3 });
    }
  },
  // Bullet-time: растянутый свист падения последнего блока; эмбиент приседает
  whoosh() {
    play('whoosh', { gain: 0.8 });
    if (ctx && ambientGain) {
      ambientGain.gain.setTargetAtTime(0.15, ctx.currentTime, 0.2);
      setTimeout(() => { if (ctx) ambientGain.gain.setTargetAtTime(1, ctx.currentTime, 0.6); }, 1800);
    }
  },
  // Аплодисменты зевак на победе
  applause() { play('applause', { gain: 0.7 }); },
  // Скрип крана: on/off, сам игнорирует повторные вызовы того же состояния
  creak(on) { creakLoop(on); },
};

// --- Музыка: 3 слоя одного лупа, стартуют синхронно ---
// База всегда; мелодия с 10-го этажа; верхний слой на комбо ≥ 3.
// playbackRate = скорость маятника / стартовая — темп растёт вместе с игрой.
// Композиция написана под период качания: 80 BPM ≈ базовый маятник (2 такта на цикл).
let mus = null; // { sources: {base, mel, top}, gains, rate }

function musicStartNow() {
  if (mus || !ctx || !buffers.music_base) return;
  const layers = {};
  const gains = {};
  const t0 = ctx.currentTime + 0.05;
  for (const name of ['base', 'mel', 'top']) {
    const src = ctx.createBufferSource();
    src.buffer = buffers['music_' + name];
    src.loop = true;
    const g = ctx.createGain();
    g.gain.value = name === 'base' ? 0.9 : 0;
    src.connect(g).connect(musicBus);
    src.start(t0); // один t0 на все слои — иначе разъедутся по фазе
    layers[name] = src;
    gains[name] = g;
  }
  mus = { layers, gains, rate: 1 };
}

function musicSetLayer(name, on) {
  if (!mus || !ctx) return;
  mus.gains[name].gain.setTargetAtTime(on ? 0.85 : 0, ctx.currentTime, 0.6);
}

export const music = {
  // Идемпотентный старт: зовётся на каждом тапе, реально стартует один раз
  start() {
    if (mus) return;
    if (buffers.music_base) musicStartNow();
    else onReadyQueue.push(() => { if (this._wanted) musicStartNow(); });
    this._wanted = true;
  },
  stop(fadeSec = 0.5) {
    this._wanted = false;
    if (!mus || !ctx) { mus = null; return; }
    const m = mus;
    mus = null;
    for (const g of Object.values(m.gains)) g.gain.setTargetAtTime(0, ctx.currentTime, fadeSec / 3);
    setTimeout(() => {
      for (const s of Object.values(m.layers)) { try { s.stop(); } catch (e) { /* уже */ } }
    }, fadeSec * 1000 + 200);
  },
  // Слои по прогрессу: этажи включают мелодию, комбо — верхний слой
  setProgress(floors, combo) {
    musicSetLayer('mel', floors >= 10);
    musicSetLayer('top', combo >= 3);
  },
  // Темп за маятником; клиппим, чтобы на марафонах музыка не превращалась в писк
  setRate(r) {
    if (!mus || !ctx) return;
    const rate = Math.max(0.9, Math.min(1.3, r));
    if (Math.abs(rate - mus.rate) < 0.01) return;
    mus.rate = rate;
    for (const s of Object.values(mus.layers)) s.playbackRate.setTargetAtTime(rate, ctx.currentTime, 0.4);
  },
  // Обрыв перед последним блоком: тишина 0.5с — удар — фанфары
  dropout() {
    if (!mus || !ctx) return;
    for (const g of Object.values(mus.gains)) g.gain.setTargetAtTime(0, ctx.currentTime, 0.12);
    this._wanted = false;
    const m = mus;
    mus = null;
    setTimeout(() => { for (const s of Object.values(m.layers)) { try { s.stop(); } catch (e) { /* уже */ } } }, 800);
  },
  // Эмбиент города: свой оттенок на группу тем (день/ночь)
  ambient(theme) {
    if (!ctx) { this._pendingTheme = theme; onReadyQueue.push(() => this.ambient(this._pendingTheme)); return; }
    const night = /night|neon|moscow/.test(theme || '');
    const name = night ? 'ambient_night' : 'ambient_day';
    if (this._ambName === name && this._amb) return;
    if (this._amb) { const a = this._amb; try { a.stop(ctx.currentTime + 0.6); } catch (e) { /* уже */ } }
    this._amb = null;
    this._ambName = name;
    if (!buffers[name]) { onReadyQueue.push(() => { if (this._ambName === name) { this._ambName = null; this.ambient(theme); } }); return; }
    const src = ctx.createBufferSource();
    src.buffer = buffers[name];
    src.loop = true;
    const g = ctx.createGain();
    g.gain.value = 0;
    g.gain.setTargetAtTime(0.35, ctx.currentTime, 0.8);
    src.connect(g).connect(ambientGain);
    src.start();
    this._amb = src;
  },
};
